import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useTheme } from "@/context/ThemeContext";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=guest, object=logged in
  const [loading, setLoading] = useState(true);
  const [authOpen, setAuthOpen] = useState(false);
  const { setTheme } = useTheme();

  const openAuth = useCallback(() => setAuthOpen(true), []);
  const closeAuth = useCallback(() => setAuthOpen(false), []);

  useEffect(() => {
    const token = localStorage.getItem("tfl_token");
    if (!token) { setLoading(false); return; }
    api.get("/auth/me")
      .then((r) => { setUser(r.data.user); if (r.data.user.theme) setTheme(r.data.user.theme); })
      .catch(() => localStorage.removeItem("tfl_token"))
      .finally(() => setLoading(false));
  }, []);

  const persist = (data) => {
    localStorage.setItem("tfl_token", data.token);
    setUser(data.user);
    if (data.user.theme) setTheme(data.user.theme);
    setAuthOpen(false);
  };

  const changeTheme = useCallback((t) => {
    setTheme(t);
    if (user) {
      api.put("/auth/profile", { theme: t }).then((r) => setUser(r.data.user)).catch(() => {});
    }
  }, [user, setTheme]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    persist(data);
    toast.success(`Bienvenue, ${data.user.name} !`);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    persist(data);
    toast.success("Compte créé avec succès !");
    return data.user;
  };

  const social = async (provider, account_type = "particulier") => {
    const { data } = await api.post("/auth/social", { provider, account_type });
    persist(data);
    toast.success(`Connecté via ${provider} (démo)`);
    return data.user;
  };

  const forgot = async (email) => {
    const { data } = await api.post("/auth/forgot-password", { email });
    toast.success(data.message);
  };

  const logout = () => {
    localStorage.removeItem("tfl_token");
    setUser(null);
    toast("Déconnecté");
  };

  const updateProfile = async (payload) => {
    const { data } = await api.put("/auth/profile", payload);
    setUser(data.user);
    toast.success("Profil mis à jour");
    return data.user;
  };

  const toggleFavorite = async (productId) => {
    if (!user) { openAuth(); return; }
    try {
      const { data } = await api.post(`/favorites/${productId}`);
      setUser((u) => ({ ...u, favorites: data.favorites }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <AuthContext.Provider value={{
      user, loading, authOpen, openAuth, closeAuth,
      login, register, social, forgot, logout, updateProfile, toggleFavorite, changeTheme,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
